import { useState, useEffect } from "react"
import { CheckCircle2, Loader2, Plus, X, MoreVertical, Pencil, Trash2, Banknote, Building2, CalendarDays } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"

import {
  useRentRecords,
  useExpenses,
  useUpdateRentRecord,
  useDeleteRentRecord,
  useCreateRentRecord,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
  useManualEarnings,
  useCreateManualEarning,
  useUpdateManualEarning,
  useDeleteManualEarning,
} from "@/features/finances/hooks/useFinances"
import { useProperties } from "@/features/properties/hooks/useProperties"
import { useLeases } from "@/features/leases/hooks/useLeases"
import type { Lease } from "@rentbase/shared"
import { APP_NAME } from "@/lib/appConfig"
import { useCurrency } from "@/hooks/useCurrency"
import { PageHeader } from "@/components/shared/PageHeader"
import { EmptyState } from "@/components/shared/EmptyState"
import { ListSkeleton } from "@/components/shared/LoadingSkeleton"
import { StatusBadge } from "@/components/shared/StatusBadge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import type { RentRecord, Expense, ManualEarning } from "@/features/finances/services/financesService"

// ─── Constants ───────────────────────────────────────────────────────────────

const EXPENSE_CATEGORIES = [
  "Repairs & Maintenance",
  "Insurance",
  "Utilities",
  "Property Tax",
  "Management Fees",
  "Advertising",
  "Cleaning",
  "Landscaping",
  "Legal & Professional",
  "Supplies",
  "Other",
]

const MANUAL_EARNING_CATEGORIES = [
  "Parking",
  "Laundry",
  "Storage",
  "Late Fee",
  "Deposit",
  "Pet Fee",
  "Key / Fob Replacement",
  "Amenity Fee",
  "Other",
]

const RENT_STATUSES = ["pending", "paid", "partial", "late", "unpaid", "cancelled"] as const

// ─── Helpers ─────────────────────────────────────────────────────────────────

function formatDate(dateStr?: string | null) {
  if (!dateStr) return ""
  return new Date(dateStr).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

// ─── Expense Sheets ───────────────────────────────────────────────────────────

const expenseFormSchema = z.object({
  category: z.string().min(1, "Category is required"),
  amount: z.coerce.number().positive("Amount must be positive"),
  date: z.string().min(1, "Date is required"),
  description: z.string().optional(),
  property_id: z.string().optional(),
  vendor_name: z.string().optional(),
})
type ExpenseFormValues = z.infer<typeof expenseFormSchema>

function ExpenseSheet({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  editing?: Expense | null
}) {
  const createExpense = useCreateExpense()
  const updateExpense = useUpdateExpense()
  const { data: properties } = useProperties()
  const { currency } = useCurrency()
  const isPending = createExpense.isPending || updateExpense.isPending

  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: {
      category: "",
      amount: undefined,
      date: new Date().toISOString().split("T")[0],
      description: "",
      property_id: "none",
      vendor_name: "",
    },
  })

  useEffect(() => {
    if (open && editing) {
      form.reset({
        category: editing.category,
        amount: editing.amount,
        date: editing.date,
        description: editing.description ?? "",
        property_id: editing.property_id ?? "none",
        vendor_name: "",
      })
    } else if (open && !editing) {
      form.reset({
        category: "",
        amount: undefined,
        date: new Date().toISOString().split("T")[0],
        description: "",
        property_id: "none",
        vendor_name: "",
      })
    }
  }, [open, editing])

  function onSubmit(values: ExpenseFormValues) {
    const payload: Record<string, unknown> = {
      category: values.category,
      amount: values.amount,
      date: values.date,
      currency,
    }
    if (values.description) payload.description = values.description
    if (values.vendor_name) payload.vendor_name = values.vendor_name
    if (values.property_id && values.property_id !== "none") payload.property_id = values.property_id

    if (editing) {
      updateExpense.mutate({ id: editing.id, input: payload }, {
        onSuccess: () => { toast.success("Expense updated"); onOpenChange(false) },
        onError: () => toast.error("Failed to update expense"),
      })
    } else {
      createExpense.mutate(payload, {
        onSuccess: () => { toast.success("Expense added"); form.reset(); onOpenChange(false) },
        onError: () => toast.error("Failed to add expense"),
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>{editing ? "Edit Expense" : "Add Expense"}</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField control={form.control} name="category" render={({ field }) => (
              <FormItem>
                <FormLabel>Category *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="amount" render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl><Input type="number" step="0.01" placeholder="0.00" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="date" render={({ field }) => (
                <FormItem>
                  <FormLabel>Date *</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="property_id" render={({ field }) => (
              <FormItem>
                <FormLabel>Property</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="All properties (general)" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="none">All properties (general)</SelectItem>
                    {properties?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="vendor_name" render={({ field }) => (
              <FormItem>
                <FormLabel>Vendor / Payee</FormLabel>
                <FormControl><Input placeholder="e.g. City Plumbing Co." {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl><Textarea placeholder="Optional notes..." rows={2} className="resize-none" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <Button type="submit" className="w-full h-12 rounded-xl" disabled={isPending}>
              {isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : (editing ? "Save Changes" : "Add Expense")}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// ─── Manual Earning Sheet ─────────────────────────────────────────────────────

const manualEarningSchema = z.object({
  category: z.string().min(1, "Category is required"),
  amount: z.coerce.number().positive("Amount must be positive"),
  date: z.string().min(1, "Date is required"),
  description: z.string().optional(),
  property_id: z.string().optional(),
})
type ManualEarningFormValues = z.infer<typeof manualEarningSchema>

function ManualEarningSheet({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  editing?: ManualEarning | null
}) {
  const createManualEarning = useCreateManualEarning()
  const updateManualEarning = useUpdateManualEarning()
  const { data: properties } = useProperties()
  const { currency } = useCurrency()
  const isPending = createManualEarning.isPending || updateManualEarning.isPending

  const form = useForm<ManualEarningFormValues>({
    resolver: zodResolver(manualEarningSchema),
    defaultValues: {
      category: "",
      amount: undefined,
      date: new Date().toISOString().split("T")[0],
      description: "",
      property_id: "none",
    },
  })

  useEffect(() => {
    if (open && editing) {
      form.reset({
        category: editing.category,
        amount: editing.amount,
        date: editing.date,
        description: editing.description ?? "",
        property_id: editing.property_id ?? "none",
      })
    } else if (open && !editing) {
      form.reset({
        category: "",
        amount: undefined,
        date: new Date().toISOString().split("T")[0],
        description: "",
        property_id: "none",
      })
    }
  }, [open, editing])

  function onSubmit(values: ManualEarningFormValues) {
    const payload: Record<string, unknown> = {
      category: values.category,
      amount: values.amount,
      date: values.date,
      currency,
    }
    if (values.description) payload.description = values.description
    if (values.property_id && values.property_id !== "none") payload.property_id = values.property_id

    if (editing) {
      updateManualEarning.mutate({ id: editing.id, input: payload }, {
        onSuccess: () => { toast.success("Earning updated"); onOpenChange(false) },
        onError: () => toast.error("Failed to update earning"),
      })
    } else {
      createManualEarning.mutate(payload, {
        onSuccess: () => { toast.success("Manual earning added"); form.reset(); onOpenChange(false) },
        onError: () => toast.error("Failed to add earning"),
      })
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-2">
          <SheetTitle>{editing ? "Edit Manual Earning" : "Add Manual Earning"}</SheetTitle>
          {!editing && (
            <p className="text-xs text-amber-600 font-medium flex items-center gap-1 mt-1">
              <Banknote className="h-3.5 w-3.5" />
              This is a manual earning entry — not linked to any lease or tenant.
            </p>
          )}
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8 mt-4">
            <FormField control={form.control} name="category" render={({ field }) => (
              <FormItem>
                <FormLabel>Category *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {MANUAL_EARNING_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="amount" render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl><Input type="number" step="0.01" placeholder="0.00" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="date" render={({ field }) => (
                <FormItem>
                  <FormLabel>Date *</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="property_id" render={({ field }) => (
              <FormItem>
                <FormLabel>Property</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Not property-specific" /></SelectTrigger></FormControl>
                  <SelectContent>
                    <SelectItem value="none">Not property-specific</SelectItem>
                    {properties?.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="description" render={({ field }) => (
              <FormItem>
                <FormLabel>Description</FormLabel>
                <FormControl><Textarea placeholder="e.g. Parking space rent for March" rows={2} className="resize-none" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <Button type="submit" className="w-full h-12 rounded-xl" disabled={isPending}>
              {isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : (editing ? "Save Changes" : "Add Manual Earning")}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// ─── Add Rent Record Sheet ────────────────────────────────────────────────────

type LeaseWithRelations = Lease & {
  tenant?: { id?: string; full_name?: string | null; email?: string | null }
  property?: { id?: string; name?: string | null; city?: string | null }
}

const addRentRecordSchema = z.object({
  lease_id: z.string().uuid("Select a lease"),
  month: z.string().min(7, "Select a month"),
  amount: z.coerce.number().positive("Amount must be positive"),
  currency: z.string().length(3),
  status: z.enum(RENT_STATUSES).default("pending"),
  paid_date: z.string().optional(),
  payment_method: z.string().optional(),
  notes: z.string().optional(),
})
type AddRentRecordFormValues = z.infer<typeof addRentRecordSchema>

function AddRentRecordSheet({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
}) {
  const createRentRecord = useCreateRentRecord()
  const { data: allLeases } = useLeases()
  const activeLeases = (allLeases as LeaseWithRelations[] | undefined)?.filter((l) => l.status === "active") ?? []

  const form = useForm<AddRentRecordFormValues>({
    resolver: zodResolver(addRentRecordSchema),
    defaultValues: {
      lease_id: "",
      month: new Date().toISOString().slice(0, 7), // YYYY-MM
      amount: undefined,
      currency: "EUR",
      status: "pending",
      paid_date: "",
      payment_method: "",
      notes: "",
    },
  })

  const watchedLeaseId = form.watch("lease_id")
  const watchedMonth = form.watch("month")
  const watchedStatus = form.watch("status")

  // When lease changes, auto-fill amount + currency
  useEffect(() => {
    if (!watchedLeaseId) return
    const lease = activeLeases.find((l) => l.id === watchedLeaseId)
    if (lease) {
      form.setValue("amount", lease.monthly_rent)
      form.setValue("currency", lease.currency)
    }
  }, [watchedLeaseId])

  // Load existing records for selected lease to warn on duplicate month
  const { data: existingRecords } = useRentRecords(watchedLeaseId || undefined)

  const duplicateWarning = (() => {
    if (!watchedLeaseId || !watchedMonth || !existingRecords) return false
    // Due dates are stored in the NEXT month, so check that month
    const [y, m] = watchedMonth.split("-").map(Number)
    const dueM = m === 12 ? 1 : m + 1
    const dueY = m === 12 ? y + 1 : y
    const duePfx = `${dueY}-${String(dueM).padStart(2, "0")}`
    return existingRecords.some((r) => r.due_date?.startsWith(duePfx))
  })()

  useEffect(() => {
    if (!open) {
      form.reset({
        lease_id: "",
        month: new Date().toISOString().slice(0, 7),
        amount: undefined,
        currency: "EUR",
        status: "pending",
        paid_date: "",
        payment_method: "",
        notes: "",
      })
    }
  }, [open])

  function onSubmit(values: AddRentRecordFormValues) {
    const [yearStr, monthStr] = values.month.split("-")
    const year = parseInt(yearStr)
    const monthIdx = parseInt(monthStr) - 1
    const lease = activeLeases.find((l) => l.id === values.lease_id)
    const dueDay = lease?.payment_due_day ?? 1
    // Due date is in the NEXT month after the billing period
    // e.g. April billing (monthIdx=3) → due in May (dueMonthIdx=4)
    const dueMonthIdx = monthIdx === 11 ? 0 : monthIdx + 1
    const dueYear = monthIdx === 11 ? year + 1 : year
    const lastDay = new Date(dueYear, dueMonthIdx + 1, 0).getDate()
    const actual = Math.min(dueDay, lastDay)
    const due_date = `${dueYear}-${String(dueMonthIdx + 1).padStart(2, "0")}-${String(actual).padStart(2, "0")}`

    const payload: Record<string, unknown> = {
      lease_id: values.lease_id,
      due_date,
      amount: values.amount,
      currency: values.currency,
      status: values.status,
    }
    if (values.paid_date) payload.paid_date = values.paid_date
    if (values.payment_method) payload.payment_method = values.payment_method
    if (values.notes) payload.notes = values.notes

    createRentRecord.mutate(payload, {
      onSuccess: () => { toast.success("Rent record added"); onOpenChange(false) },
      onError: () => toast.error("Failed to add rent record"),
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[85dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>Add Rent Record</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <FormField control={form.control} name="lease_id" render={({ field }) => (
              <FormItem>
                <FormLabel>Lease *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue placeholder="Select a lease" /></SelectTrigger></FormControl>
                  <SelectContent>
                    {activeLeases.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.tenant?.full_name ?? "Unknown"} — {l.property?.name ?? "Unknown"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            <FormField control={form.control} name="month" render={({ field }) => (
              <FormItem>
                <FormLabel>Month *</FormLabel>
                <FormControl>
                  <Input type="month" {...field} />
                </FormControl>
                {duplicateWarning && (
                  <p className="text-xs text-amber-600 font-medium mt-1">
                    A rent record already exists for this month. Submitting will create an additional one.
                  </p>
                )}
                <FormMessage />
              </FormItem>
            )} />

            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="amount" render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl><Input type="number" step="0.01" placeholder="0.00" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="currency" render={({ field }) => (
                <FormItem>
                  <FormLabel>Currency</FormLabel>
                  <FormControl><Input maxLength={3} {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>

            <FormField control={form.control} name="status" render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    {RENT_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />

            {(watchedStatus === "paid" || watchedStatus === "partial") && (
              <>
                <FormField control={form.control} name="paid_date" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Paid Date</FormLabel>
                    <FormControl><Input type="date" {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
                <FormField control={form.control} name="payment_method" render={({ field }) => (
                  <FormItem>
                    <FormLabel>Payment Method</FormLabel>
                    <FormControl><Input placeholder="e.g. Bank transfer, Cash..." {...field} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </>
            )}

            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl><Textarea placeholder="Optional notes..." rows={2} className="resize-none" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />

            <Button type="submit" className="w-full h-12 rounded-xl" disabled={createRentRecord.isPending}>
              {createRentRecord.isPending
                ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Adding...</>
                : "Add Rent Record"}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// ─── Edit Rent Record Sheet ───────────────────────────────────────────────────

const editRentSchema = z.object({
  amount: z.coerce.number().positive("Amount must be positive"),
  status: z.enum(RENT_STATUSES),
  due_date: z.string().min(1),
  paid_date: z.string().optional(),
  payment_method: z.string().optional(),
  notes: z.string().optional(),
})
type EditRentFormValues = z.infer<typeof editRentSchema>

function EditRentRecordSheet({
  open,
  onOpenChange,
  record,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  record: RentRecord | null
}) {
  const updateRentRecord = useUpdateRentRecord()

  const form = useForm<EditRentFormValues>({
    resolver: zodResolver(editRentSchema),
    defaultValues: { amount: 0, status: "pending", due_date: "", paid_date: "", payment_method: "", notes: "" },
  })

  useEffect(() => {
    if (open && record) {
      form.reset({
        amount: record.amount,
        status: record.status,
        due_date: record.due_date,
        paid_date: record.paid_date ?? "",
        payment_method: "",
        notes: record.notes ?? "",
      })
    }
  }, [open, record])

  function onSubmit(values: EditRentFormValues) {
    if (!record) return
    const payload: Record<string, unknown> = {
      amount: values.amount,
      status: values.status,
      due_date: values.due_date,
    }
    if (values.paid_date) payload.paid_date = values.paid_date
    if (values.payment_method) payload.payment_method = values.payment_method
    if (values.notes) payload.notes = values.notes

    updateRentRecord.mutate({ id: record.id, input: payload }, {
      onSuccess: () => { toast.success("Rent record updated"); onOpenChange(false) },
      onError: () => toast.error("Failed to update record"),
    })
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="h-[80dvh] overflow-y-auto rounded-t-2xl">
        <SheetHeader className="mb-4">
          <SheetTitle>Edit Rent Record</SheetTitle>
        </SheetHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 pb-8">
            <div className="grid grid-cols-2 gap-3">
              <FormField control={form.control} name="amount" render={({ field }) => (
                <FormItem>
                  <FormLabel>Amount *</FormLabel>
                  <FormControl><Input type="number" step="0.01" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
              <FormField control={form.control} name="due_date" render={({ field }) => (
                <FormItem>
                  <FormLabel>Due Date *</FormLabel>
                  <FormControl><Input type="date" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )} />
            </div>
            <FormField control={form.control} name="status" render={({ field }) => (
              <FormItem>
                <FormLabel>Status *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl><SelectTrigger><SelectValue /></SelectTrigger></FormControl>
                  <SelectContent>
                    {RENT_STATUSES.map((s) => (
                      <SelectItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="paid_date" render={({ field }) => (
              <FormItem>
                <FormLabel>Paid Date</FormLabel>
                <FormControl><Input type="date" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="payment_method" render={({ field }) => (
              <FormItem>
                <FormLabel>Payment Method</FormLabel>
                <FormControl><Input placeholder="e.g. Bank transfer, Cash..." {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <FormField control={form.control} name="notes" render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl><Textarea placeholder="Optional notes..." rows={2} className="resize-none" {...field} /></FormControl>
                <FormMessage />
              </FormItem>
            )} />
            <Button type="submit" className="w-full h-12 rounded-xl" disabled={updateRentRecord.isPending}>
              {updateRentRecord.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : "Save Changes"}
            </Button>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  )
}

// ─── Delete Confirm Dialog ────────────────────────────────────────────────────

function DeleteDialog({
  open,
  onOpenChange,
  title,
  onConfirm,
  loading,
}: {
  open: boolean
  onOpenChange: (v: boolean) => void
  title: string
  onConfirm: () => void
  loading: boolean
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {title}?</AlertDialogTitle>
          <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} disabled={loading} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

// ─── Rent Tab ─────────────────────────────────────────────────────────────────

const RENT_FILTERS = ["all", "pending", "paid", "late", "cancelled"] as const
type RentFilter = (typeof RENT_FILTERS)[number]

function RentTab({
  onAddManualEarning,
}: {
  onAddManualEarning: () => void
}) {
  const { data: records, isLoading: rentLoading } = useRentRecords()
  const { data: manualEarnings, isLoading: earningsLoading } = useManualEarnings()
  const updateRentRecord = useUpdateRentRecord()
  const deleteRentRecord = useDeleteRentRecord()
  const deleteManualEarning = useDeleteManualEarning()
  const { format } = useCurrency()
  const [filter, setFilter] = useState<RentFilter>("all")

  const [addRentOpen, setAddRentOpen] = useState(false)
  const [editingRent, setEditingRent] = useState<RentRecord | null>(null)
  const [editRentOpen, setEditRentOpen] = useState(false)
  const [editingEarning, setEditingEarning] = useState<ManualEarning | null>(null)
  const [editEarningOpen, setEditEarningOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ type: "rent" | "earning"; id: string } | null>(null)

  const isLoading = rentLoading || earningsLoading

  const filteredRent =
    filter === "all" ? records : records?.filter((r) => r.status === filter)

  function markPaid(record: RentRecord) {
    updateRentRecord.mutate(
      { id: record.id, input: { status: "paid", paid_date: new Date().toISOString() } },
      {
        onSuccess: () => toast.success("Marked as paid"),
        onError: () => toast.error("Failed to update record"),
      }
    )
  }

  function markCancelled(record: RentRecord) {
    updateRentRecord.mutate(
      { id: record.id, input: { status: "cancelled" } },
      {
        onSuccess: () => toast.success("Payment cancelled"),
        onError: () => toast.error("Failed to cancel payment"),
      }
    )
  }

  function confirmDelete() {
    if (!deleteTarget) return
    if (deleteTarget.type === "rent") {
      deleteRentRecord.mutate(deleteTarget.id, {
        onSuccess: () => { toast.success("Rent record deleted"); setDeleteTarget(null) },
        onError: () => toast.error("Failed to delete"),
      })
    } else {
      deleteManualEarning.mutate(deleteTarget.id, {
        onSuccess: () => { toast.success("Manual earning deleted"); setDeleteTarget(null) },
        onError: () => toast.error("Failed to delete"),
      })
    }
  }

  const deleteLoading = deleteRentRecord.isPending || deleteManualEarning.isPending

  return (
    <div>
      {/* Add Rent Record button */}
      <div className="flex justify-end mb-3">
        <Button size="sm" className="h-9 rounded-xl gap-1" onClick={() => setAddRentOpen(true)}>
          <Plus className="h-4 w-4" />Add Rent Record
        </Button>
      </div>

      {/* Filter chips */}
      <div className="flex gap-2 overflow-x-auto pb-4 pt-1 px-1 scrollbar-none snap-x">
        {RENT_FILTERS.map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`shrink-0 snap-center h-9 px-5 rounded-full text-sm font-bold transition-all shadow-sm ${filter === f
              ? "bg-primary text-primary-foreground shadow-glow scale-105"
              : "bg-card/50 backdrop-blur-md border border-border/50 text-muted-foreground hover:bg-muted/80 hover:text-foreground"
              }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
      </div>

      {isLoading && <ListSkeleton count={3} />}

      {/* Rent Records */}
      {!isLoading && (
        <>
          {filteredRent && filteredRent.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
              {filteredRent.map((record) => (
                <Card key={record.id} className="rounded-3xl border border-border/50 bg-card/60 backdrop-blur-xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col group overflow-hidden">
                  <CardContent className="p-5 flex flex-col h-full gap-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 pr-2 pb-1">
                        <h3 className="text-[17px] font-bold leading-tight truncate tracking-tight mb-1">
                          {record.lease?.tenant?.full_name ?? "Unknown tenant"}
                        </h3>
                        <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground font-medium truncate">
                           <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
                           <span className="truncate">{record.lease?.property?.name ?? "Unknown property"}</span>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full -mr-2 -mt-2 shrink-0 text-muted-foreground hover:text-foreground">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setEditingRent(record); setEditRentOpen(true) }}>
                            <Pencil className="h-4 w-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteTarget({ type: "rent", id: record.id })}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex items-end justify-between mt-auto pt-2">
                      <div className="flex flex-col">
                        <span className="text-[11px] text-muted-foreground mb-1 font-bold uppercase tracking-wider">Due {formatDate(record.due_date)}</span>
                        <span className="text-2xl font-extrabold text-foreground tracking-tight leading-none">{format(record.amount)}</span>
                      </div>
                      <StatusBadge status={record.status} />
                    </div>

                    {record.status !== "paid" && record.status !== "cancelled" && (
                      <div className="grid grid-cols-2 gap-2 pt-3 border-t border-border/50 mt-2">
                        <Button
                          variant="ghost"
                          className="h-9 rounded-xl text-[13px] font-bold bg-primary/10 text-primary hover:bg-primary/20 w-full hover:-translate-y-0.5 transition-transform"
                          onClick={() => markPaid(record)}
                          disabled={updateRentRecord.isPending}
                        >
                          {updateRentRecord.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />}
                          Mark Paid
                        </Button>
                        <Button
                          variant="ghost"
                          className="h-9 rounded-xl text-[13px] font-bold bg-destructive/10 text-destructive hover:bg-destructive/20 w-full hover:-translate-y-0.5 transition-transform"
                          onClick={() => markCancelled(record)}
                          disabled={updateRentRecord.isPending}
                        >
                          <X className="h-3.5 w-3.5 mr-1.5" />
                          Cancel
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {(!filteredRent || filteredRent.length === 0) && (!manualEarnings || manualEarnings.length === 0) && (
            <EmptyState
              icon={<CheckCircle2 className="h-9 w-9 text-muted-foreground" />}
              title="No rent records"
              description="Rent records will appear here once leases are active."
            />
          )}
        </>
      )}

      {/* Manual Earnings Section */}
      {!isLoading && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-bold text-foreground">Other Earnings</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-semibold border border-amber-200">Manual</span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 rounded-xl gap-1 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={onAddManualEarning}
            >
              <Plus className="h-3.5 w-3.5" />
              Add Manual Earning
            </Button>
          </div>

          {manualEarnings && manualEarnings.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
              {manualEarnings.map((earning) => (
                <Card key={earning.id} className="rounded-3xl border border-amber-200/60 bg-amber-50/40 dark:bg-amber-900/10 backdrop-blur-xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col group overflow-hidden">
                  <CardContent className="p-5 flex flex-col h-full gap-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 pr-2 pb-1">
                        <div className="flex items-center gap-2 mb-1.5">
                           <h3 className="text-[17px] font-bold leading-tight truncate tracking-tight text-foreground">
                             {earning.description ?? earning.category}
                           </h3>
                           <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 font-bold shrink-0 border border-amber-200 uppercase tracking-wider">Manual</span>
                        </div>
                        <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground font-medium truncate">
                           <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
                           <span className="truncate">{earning.property?.name ?? "No property"}</span>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full -mr-2 -mt-2 shrink-0 text-muted-foreground hover:text-foreground">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => { setEditingEarning(earning); setEditEarningOpen(true) }}>
                            <Pencil className="h-4 w-4 mr-2" />Edit
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => setDeleteTarget({ type: "earning", id: earning.id })}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="flex items-end justify-between mt-auto pt-2">
                      <div className="flex flex-col">
                        <span className="text-[11px] text-muted-foreground mb-1 font-bold uppercase tracking-wider">{formatDate(earning.date)}</span>
                        <span className="text-2xl font-extrabold text-foreground tracking-tight leading-none">{format(earning.amount)}</span>
                      </div>
                      <span className="text-[12px] font-extrabold text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20 px-3 py-1.5 rounded-xl uppercase tracking-wider">
                        Collected
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground text-center py-4">
              No manual earnings yet. Use the button above to add parking fees, laundry income, etc.
            </p>
          )}
        </div>
      )}

      <AddRentRecordSheet open={addRentOpen} onOpenChange={setAddRentOpen} />
      <EditRentRecordSheet open={editRentOpen} onOpenChange={setEditRentOpen} record={editingRent} />
      <ManualEarningSheet open={editEarningOpen} onOpenChange={setEditEarningOpen} editing={editingEarning} />
      <DeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null) }}
        title={deleteTarget?.type === "rent" ? "rent record" : "manual earning"}
        onConfirm={confirmDelete}
        loading={deleteLoading}
      />
    </div>
  )
}

// ─── Expense Tab ──────────────────────────────────────────────────────────────

function ExpenseTab({ onAdd }: { onAdd: () => void }) {
  const { data: expenses, isLoading } = useExpenses()
  const deleteExpense = useDeleteExpense()
  const { format } = useCurrency()

  const [editingExpense, setEditingExpense] = useState<Expense | null>(null)
  const [editOpen, setEditOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null)

  function confirmDelete() {
    if (!deleteTarget) return
    deleteExpense.mutate(deleteTarget, {
      onSuccess: () => { toast.success("Expense deleted"); setDeleteTarget(null) },
      onError: () => toast.error("Failed to delete expense"),
    })
  }

  return (
    <div>
      {isLoading && <ListSkeleton count={3} />}

      <div className="flex justify-end mb-4">
        <Button size="sm" className="h-9 rounded-xl gap-1" onClick={onAdd}>
          <Plus className="h-4 w-4" />Add Expense
        </Button>
      </div>

      {!isLoading && !expenses?.length && (
        <EmptyState
          icon={<CheckCircle2 className="h-9 w-9 text-muted-foreground" />}
          title="No expenses yet"
          description="Track your property expenses here."
          action={{ label: "Add Expense", onClick: onAdd }}
        />
      )}

      {!isLoading && expenses && expenses.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-4">
          {expenses.map((expense) => (
            <Card key={expense.id} className="rounded-3xl border border-destructive/20 dark:border-destructive/30 bg-destructive/5 dark:bg-destructive/10 backdrop-blur-xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all flex flex-col group overflow-hidden">
              <CardContent className="p-5 flex flex-col h-full gap-4">
                 <div className="flex items-start justify-between gap-3">
                   <div className="min-w-0 pr-2 pb-1">
                     <h3 className="text-[17px] font-bold leading-tight truncate tracking-tight mb-1 text-foreground">
                       {expense.description ?? expense.category}
                     </h3>
                     <div className="flex items-center gap-1.5 text-[13px] text-muted-foreground font-medium truncate">
                        <Building2 className="h-3.5 w-3.5 shrink-0 opacity-70" />
                        <span className="truncate">{expense.property?.name ?? "General Expense"}</span>
                     </div>
                   </div>
                   <DropdownMenu>
                     <DropdownMenuTrigger asChild>
                       <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full -mr-2 -mt-2 shrink-0 text-muted-foreground hover:text-foreground">
                         <MoreVertical className="h-4 w-4" />
                       </Button>
                     </DropdownMenuTrigger>
                     <DropdownMenuContent align="end">
                       <DropdownMenuItem onClick={() => { setEditingExpense(expense); setEditOpen(true) }}>
                         <Pencil className="h-4 w-4 mr-2" />Edit
                       </DropdownMenuItem>
                       <DropdownMenuSeparator />
                       <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(expense.id)}>
                         <Trash2 className="h-4 w-4 mr-2" />Delete
                       </DropdownMenuItem>
                     </DropdownMenuContent>
                   </DropdownMenu>
                 </div>
                 
                 <div className="flex items-end justify-between mt-auto pt-2">
                   <div className="flex flex-col">
                     <span className="text-[11px] text-muted-foreground mb-1 font-bold uppercase tracking-wider">{formatDate(expense.date)}</span>
                     <span className="text-2xl font-extrabold text-foreground tracking-tight leading-none">{format(expense.amount)}</span>
                   </div>
                   <span className="text-[12px] font-extrabold text-destructive bg-destructive/10 px-3 py-1.5 rounded-xl uppercase tracking-wider">
                     Spent
                   </span>
                 </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ExpenseSheet open={editOpen} onOpenChange={setEditOpen} editing={editingExpense} />
      <DeleteDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null) }}
        title="expense"
        onConfirm={confirmDelete}
        loading={deleteExpense.isPending}
      />
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FinancesPage() {
  const [expenseSheetOpen, setExpenseSheetOpen] = useState(false)
  const [manualEarningSheetOpen, setManualEarningSheetOpen] = useState(false)
  const { data: records } = useRentRecords()
  const { data: manualEarnings } = useManualEarnings()
  const { data: expenses } = useExpenses()
  const { format } = useCurrency()

  useEffect(() => {
    const prev = document.title
    document.title = `Finances | ${APP_NAME}`
    return () => { document.title = prev }
  }, [])

  const stats = {
    expected: records?.reduce((acc, r) => acc + (Number(r.amount) || 0), 0) ?? 0,
    collected:
      (records?.filter(r => r.status === "paid").reduce((acc, r) => acc + (Number(r.amount) || 0), 0) ?? 0) +
      (manualEarnings?.reduce((acc, e) => acc + (Number(e.amount) || 0), 0) ?? 0),
    expenses: expenses?.reduce((acc, exp) => acc + (Number(exp.amount) || 0), 0) ?? 0,
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader title="Finances" />

      <div className="flex-1 overflow-y-auto pb-nav px-4 pt-4">
        {/* Top-Level Stats */}
        <div className="mb-6">
          <div className="flex bg-card/60 backdrop-blur-xl border border-border/50 rounded-2xl shadow-soft divide-x divide-border/50 overflow-hidden">
            <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">Expected</span>
              <span className="text-xl font-extrabold text-foreground">{format(stats.expected)}</span>
            </div>
            <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center bg-primary/5">
              <span className="text-xs font-bold text-primary/80 uppercase tracking-widest mb-1">Collected</span>
              <span className="text-xl font-extrabold text-primary">{format(stats.collected)}</span>
            </div>
            <div className="flex-1 py-3 px-4 flex flex-col items-center justify-center text-center">
              <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest mb-1">Expenses</span>
              <span className="text-xl font-extrabold text-destructive">{format(stats.expenses)}</span>
            </div>
          </div>
        </div>

        <div>
          <Tabs defaultValue="rent">
            <TabsList className="w-full h-12 rounded-2xl mb-5 bg-muted/50 backdrop-blur-xl p-1 relative grid grid-cols-2">
              <TabsTrigger value="rent" className="rounded-xl text-sm font-bold transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Rent & Earnings
              </TabsTrigger>
              <TabsTrigger value="expenses" className="rounded-xl text-sm font-bold transition-all data-[state=active]:bg-background data-[state=active]:shadow-sm">
                Expenses
              </TabsTrigger>
            </TabsList>

            <TabsContent value="rent" className="animate-in fade-in-50 duration-300 outline-none">
              <RentTab onAddManualEarning={() => setManualEarningSheetOpen(true)} />
            </TabsContent>
            <TabsContent value="expenses" className="animate-in fade-in-50 duration-300 outline-none">
              <ExpenseTab onAdd={() => setExpenseSheetOpen(true)} />
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <ExpenseSheet open={expenseSheetOpen} onOpenChange={setExpenseSheetOpen} />
      <ManualEarningSheet open={manualEarningSheetOpen} onOpenChange={setManualEarningSheetOpen} />
    </div>
  )
}
